import { useEffect, useRef, useCallback, useState } from "react";

export default function useEffectAsync(asyncFn, onSuccess, deps) {
  useEffect(() => {
    let isMounted = true;

    asyncFn()
      .then(data => {
        if (isMounted) onSuccess(data);
      })
      .catch(() => {
        // Swallow failures so a rejected request doesn't surface as an
        // unhandled promise rejection in the console; the component keeps
        // its current state and renders its fallback.
      });

    return () => {
      isMounted = false;
    };
  }, [...deps]);
}

// Usage:
// const isMounted = useIsMounted()
// if(isMounted()) { ... }
export function useIsMounted() {
  const mounted = useRef(false);
  const isMounted = useCallback(() => mounted.current, []);
  useEffect(() => {
    mounted.current = true;
    // eslint-disable-next-line no-return-assign
    return () => (mounted.current = false);
  }, []);
  return isMounted;
}

// Usage:
// const [state, setState] = useMountedState(...)
export function useMountedState(init) {
  const isMounted = useIsMounted();
  const [state, setState] = useState(init);
  const setStateCallback = useCallback(
    // eslint-disable-next-line consistent-return
    (...args) => {
      if (isMounted()) {
        return setState(...args);
      }
    },
    [isMounted, setState]
  );
  return [state, setStateCallback];
}

export function useInterval(callback, delay) {
  const savedCallback = useRef();

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}
